import { switchClasses } from "@mui/material";
import { grey } from "@mui/material/colors";
import { alpha, createTheme, css, Theme } from "@mui/material/styles";

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        grey: true;
    }
}

declare module "@mui/material" {
    interface Color {
        main: string;
        dark: string;
    }

    interface SwitchProps {
        variant?: "ios";
    }
}

declare module "@mui/material/styles/createTheme" {
    interface Theme {
        customBreakPoints: {
            width: {
                lg2: number;
            };
            height: {
                sm: number;
            };
        };
        customSpacing: {
            hudPadding: number;
            hudPaddingMobile: number;
            widgetGroupPanelExpandedWidth: number;
            objectTreeExpanderPadding: number;
            widgetZIndex: number;
        };
        customShadows: {
            widgetHeader: string;
        };
        customShape: {
            hudPanelBorderRadius: number;
        };
    }

    interface ThemeOptions {
        customBreakPoints: {
            width: {
                lg2: number;
            };
            height: {
                sm: number;
            };
        };
        customSpacing: {
            hudPadding: number;
            hudPaddingMobile: number;
            widgetGroupPanelExpandedWidth: number;
            objectTreeExpanderPadding: number;
            widgetZIndex: number;
        };
        customShadows: {
            widgetHeader: string;
        };
        customShape: {
            hudPanelBorderRadius: number;
        };
    }
}

let theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 767,
            md: 1175,
            lg: 1440,
            xl: 2840,
        },
    },
    palette: {
        primary: {
            // light: will be calculated from palette.primary.main,
            main: "#D61E5C",
            // dark: will be calculated from palette.primary.main,
            // contrastText: will be calculated to contrast with palette.primary.main
        },
        secondary: {
            // light: '#0066ff',
            main: "#253746",
            // dark: will be calculated from palette.secondary.main,
            // contrastText: 'white',
        },
        text: {
            primary: "#253746",
        },
        info: {
            main: "#007DFF",
        },
        grey: {
            main: grey[300],
            dark: grey[400],
        },
        // Used by `getContrastText()` to maximize the contrast between
        // the background and the text.
        contrastThreshold: 3,
        // Used by the functions below to shift a color's luminance by approximately
        // two indexes within its tonal palette.
        // E.g., shift from Red 500 to Red 300 or Red 700.
        tonalOffset: 0.2,
    },
    typography: {
        fontFamily: ["Open Sans", "Roboto", "Helvetica", "sans-serif"].join(","),
        h6: {
            fontSize: "1.125rem",
            fontWeight: 400,
            lineHeight: 1,
        },
        body2: {
            fontWeight: 600,
        },
    },
    customShadows: {
        widgetHeader: "0px 5px 5px rgba(0, 0, 0, 0.05)",
    },
    customShape: {
        hudPanelBorderRadius: 16,
    },
    customSpacing: {
        hudPadding: 2,
        hudPaddingMobile: 1,
        widgetGroupPanelExpandedWidth: 28,
        objectTreeExpanderPadding: 0.75,
        widgetZIndex: 1051,
    },
    customBreakPoints: {
        width: {
            lg2: 1700,
        },
        height: {
            sm: 849.95,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: `
                body {
                    overscroll-behavior: contain;
                    overflow: hidden;
                    background: transparent;

                    
                }

                *, *::before, *::after {
                    touch-action: pan-x pan-y;    
                }

                label {
                    user-select: none;
                }
            `,
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: "none",
                },
            },
        },
        MuiListItem: {
            defaultProps: {
                component: "li",
            },
        },
        MuiListItemButton: {
            defaultProps: {
                component: "li",
            },
        },
        MuiMenu: {
            defaultProps: {
                slotProps: {
                    root: (state) => ({
                        onContextMenu: (evt) => {
                            evt.preventDefault();

                            if (state.onClose) {
                                state.onClose(evt, "backdropClick");
                            }
                        },
                    }),
                },
            },
        },
        MuiPopover: {
            defaultProps: {
                slotProps: {
                    root: (state) => ({
                        onContextMenu: (evt) => {
                            evt.preventDefault();

                            if (state.onClose) {
                                state.onClose(evt, "backdropClick");
                            }
                        },
                    }),
                },
            },
        },
        MuiSwitch: {
            variants: [
                {
                    props: { variant: "ios" },
                    style: ({ theme }: { theme: Theme }) => css`
                        &.${switchClasses.root} {
                            width: 48px;
                            height: 24px;
                            padding: 0;
                            margin: ${theme.spacing(1)};
                        }

                        & .${switchClasses.switchBase} {
                            padding: 1px;
                            color: ${theme.palette.grey[600]};

                            &.${switchClasses.checked} {
                                transform: translateX(23px);
                                color: ${theme.palette.primary.main};

                                & + .${switchClasses.track} {
                                    opacity: 0.1;
                                }
                            }
                        }

                        & .${switchClasses.thumb} {
                            margin-top: 2px;
                            margin-left: 3px;
                            width: 18px;
                            height: 18px;
                        }

                        & .${switchClasses.track} {
                            border-radius: ${24 / 2}px;
                            color: ${theme.palette.grey[600]};
                            opacity: 0.1;
                        }
                    `,
                },
            ],
        },
    },
});

theme = createTheme(theme, {
    components: {
        MuiButton: {
            variants: [
                {
                    props: { variant: "contained", color: "grey" },
                    style: {
                        color: theme.palette.getContrastText(theme.palette.grey[300]),
                    },
                },
                {
                    props: { variant: "outlined", color: "grey" },
                    style: {
                        color: theme.palette.text.primary,
                        borderColor:
                            theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
                        "&.Mui-disabled": {
                            border: `1px solid ${theme.palette.action.disabledBackground}`,
                        },
                        "&:hover": {
                            borderColor:
                                theme.palette.mode === "light" ? "rgba(0, 0, 0, 0.23)" : "rgba(255, 255, 255, 0.23)",
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                        },
                    },
                },
                {
                    props: { color: "grey", variant: "text" },
                    style: {
                        color: theme.palette.text.primary,
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.text.primary, theme.palette.action.hoverOpacity),
                        },
                    },
                },
            ],
        },
        MuiAppLoading: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    backgroundColor: theme.palette.secondary.main,
                }),
            },
        },
    },
});

const procoreTheme = createTheme(theme, {
    palette: {
        primary: {
            main: "#2066DF",
        },
    },
    components: {
        MuiSwitch: {
            variants: [
                {
                    props: { variant: "ios" },
                    style: ({ theme }: { theme: Theme }) => css`
                        &.${switchClasses.root} {
                            width: 36px;
                            height: 20px;
                            padding: 0;
                            margin: ${theme.spacing(1)};
                        }

                        & .${switchClasses.switchBase} {
                            padding: 1px;
                            color: ${theme.palette.grey[600]};

                            &.${switchClasses.checked} {
                                transform: translateX(14px);
                                color: white;

                                & + .${switchClasses.track} {
                                    opacity: 1;
                                }
                            }
                        }

                        & .${switchClasses.thumb} {
                            margin-top: 1px;
                            margin-left: 2px;
                            width: 16px;
                            height: 16px;
                        }

                        & .${switchClasses.track} {
                            border-radius: ${20 / 2}px;
                            color: ${theme.palette.grey[600]};
                            opacity: 0.1;
                        }
                    `,
                },
            ],
        },
        MuiAppLoading: {
            styleOverrides: {
                root: {
                    backgroundColor: "rgb(239 240 241)",
                },
            },
        },
        MuiAppLoadingSpinner: {
            // Size doesn't exactly match mid size procore spinner at https://core.procore.com/12.20.0/web/components/spinner/react/#size
            // but it is close enough.
            // Things are more complex because the spinners use different viewBox (22px vs 24px).
            // Ideally we would use the library, but pulling it just for spinner sounds like a waste.
            defaultProps: {
                size: 26,
                thickness: 7,
            },
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    color: theme.palette.primary.main,
                    ".MuiCircularProgress-circle": {
                        stroke: "rgb(145, 156, 161)",
                        strokeLinecap: "round",
                    },
                }),
            },
        },
    },
});

export { theme, procoreTheme };
